import React from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOrg } from "../context/OrgContext";
import { useTerminology } from "../context/TerminologyContext";
import PageHeader from "../components/PageHeader";
import { DateRangePicker, LotSelectionTable } from "../components/ui-helpers";
import { DetailsModal, ExpandableChart } from "../components/chart-helpers";
import { downloadCsvRows, downloadExcelRows } from "../utils/spreadsheet";
import { formatDateTime } from "../utils/datetime";
import { Line } from "react-chartjs-2";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
);

const emptyDateRange = { date_from: "", date_to: "" };
const NUMERIC_TYPES = new Set(["int", "float", "number", "numeric"]);

function splitTallCanvasToPDF(
  canvas,
  filename,
  { orientation = "landscape" } = {},
) {
  const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const scale = pw / canvas.width;
  const pageHeightPx = ph / scale;
  let rendered = 0;

  while (rendered < canvas.height) {
    const sliceHeight = Math.min(pageHeightPx, canvas.height - rendered);
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeight;
    const pctx = pageCanvas.getContext("2d");
    pctx.drawImage(
      canvas,
      0,
      rendered,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight,
    );
    const img = pageCanvas.toDataURL("image/png", 1.0);
    if (rendered > 0) pdf.addPage();
    pdf.addImage(img, "PNG", 0, 0, pw, sliceHeight * scale);
    rendered += sliceHeight;
  }

  pdf.save(filename);
}

async function captureNodeToCanvas(node) {
  return await html2canvas(node, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
    foreignObjectRendering: false,
  });
}

const cleanValue = (value) =>
  String(value ?? "")
    .normalize("NFKC")
    .trim();
const cleanKey = (value) =>
  cleanValue(value).replace(/\s+/g, " ").toLowerCase();
const skuKey = (value) =>
  cleanValue(value)
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
const realMaterialId = (row) => {
  const mid = cleanValue(row?.material_id);
  return mid && !mid.includes("::") ? mid : "";
};
const safeFilePart = (value) =>
  cleanValue(value || "chart")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "") || "chart";

const rowIdentityKey = (row) => {
  const mid = realMaterialId(row);
  if (mid) return `id:${mid}`;
  return `legacy:${cleanKey(row?.vendor)}::${cleanKey(row?.material)}::${skuKey(row?.sku)}`;
};
const normalizeMaterialRow = (row) => ({
  vendor: cleanValue(row?.vendor || row?.Vendor || row?.["Vendor Name"]),
  material: cleanValue(
    row?.material || row?.Material || row?.["Material Name"],
  ),
  sku: cleanValue(row?.sku || row?.SKU),
  material_id: realMaterialId(row),
});
const uniqueMaterialRows = (rows = []) => {
  const seen = new Set();
  const out = [];
  rows.map(normalizeMaterialRow).forEach((row) => {
    if (!row.vendor || !row.material || !row.sku) return;
    const key = rowIdentityKey(row);
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ ...row, id: key });
  });
  return out;
};
const groupLetter = (index) => {
  let n = index;
  let label = "";
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
};
const slotId = () =>
  `slot_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const groupId = () =>
  `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const clampChartCount = (value) =>
  Math.max(1, Math.min(4, parseInt(value || "1", 10) || 1));
const preventNumberWheel = (event) => event.currentTarget.blur();
const isNumericSeries = (series) => {
  if (!series) return false;
  const dtype = cleanValue(series.data_type).toLowerCase();
  return (
    NUMERIC_TYPES.has(dtype) &&
    (series.result || []).some(
      (value) => typeof value === "number" && !Number.isNaN(value),
    )
  );
};
const describeRow = (row) =>
  [row?.vendor, row?.material, row?.sku].filter(Boolean).join(" • ");

function useChartMaterialRows(user, currentOrg) {
  const [rows, setRows] = React.useState([]);

  React.useEffect(() => {
    let alive = true;
    const loadVendorRows = async (vendor) => {
      const res = await api.get(
        `/list_vendor_materials_approved/${encodeURIComponent(vendor)}`,
      );
      return (res.data?.materials || []).map((row) => ({
        ...row,
        vendor: row.vendor || vendor,
      }));
    };

    async function load() {
      try {
        if (user?.currentRole === "Vendor") {
          const vendorRows = await loadVendorRows(user.uid);
          if (alive) setRows(uniqueMaterialRows(vendorRows));
          return;
        }
        const vendorsRes = await api.get("/list_approved_vendors");
        const vendors = (vendorsRes.data?.vendors || [])
          .map((vendor) => vendor.name)
          .filter(Boolean);
        const batches = await Promise.all(
          vendors.map((vendor) => loadVendorRows(vendor).catch(() => [])),
        );
        if (alive) setRows(uniqueMaterialRows(batches.flat()));
      } catch {
        if (alive) setRows([]);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [user?.currentRole, user?.uid, currentOrg]);

  return rows;
}

function makeSlotFromRow(row) {
  return {
    id: slotId(),
    vendor: row.vendor || "",
    material: row.material || "",
    sku: row.sku || "",
    material_id: realMaterialId(row),
    rowKey: rowIdentityKey(row),
    param: "",
    dateRange: { ...emptyDateRange },
    n: 20,
    lockedSelection: true,
  };
}

function makeBlankSlot() {
  return {
    id: slotId(),
    vendor: "",
    material: "",
    sku: "",
    material_id: "",
    rowKey: "",
    param: "",
    dateRange: { ...emptyDateRange },
    n: 20,
    lockedSelection: false,
  };
}

function ChartSlot({
  slot,
  label,
  optionRows,
  onChange,
  onRemove,
  onCopyDatesToAll,
}) {
  const { current: currentOrg } = useOrg();
  const { t, tp } = useTerminology();
  const [specs, setSpecs] = React.useState([]);
  const [series, setSeries] = React.useState(null);
  const chartCanvasRef = React.useRef(null);
  const [totalLots, setTotalLots] = React.useState(0);
  const [error, setError] = React.useState("");
  const [exportError, setExportError] = React.useState("");

  const selectionReady = Boolean(slot.vendor && slot.sku);

  React.useEffect(() => {
    let alive = true;
    setSpecs([]);
    setSeries(null);
    setError("");
    if (!selectionReady)
      return () => {
        alive = false;
      };
    api
      .get("/charts/template", {
        params: {
          vendor: slot.vendor,
          sku: slot.sku,
          material_id: slot.material_id,
        },
      })
      .then((res) => {
        if (alive) setSpecs(res.data?.specs || []);
      })
      .catch(() => {
        if (alive)
          setError("Unable to load chart parameters for this selection.");
      });
    return () => {
      alive = false;
    };
  }, [selectionReady, slot.vendor, slot.sku, slot.material_id, currentOrg]);

  React.useEffect(() => {
    let alive = true;
    setTotalLots(0);
    if (!selectionReady)
      return () => {
        alive = false;
      };
    const params = {
      vendor: slot.vendor,
      sku: slot.sku,
      material_id: slot.material_id,
      date_from: slot.dateRange?.date_from || undefined,
      date_to: slot.dateRange?.date_to || undefined,
    };
    api
      .get("/charts/lots", { params })
      .then((res) => {
        if (alive) setTotalLots((res.data?.items || []).length);
      })
      .catch(() => {
        if (alive) setTotalLots(0);
      });
    return () => {
      alive = false;
    };
  }, [
    selectionReady,
    slot.vendor,
    slot.sku,
    slot.material_id,
    slot.dateRange?.date_from,
    slot.dateRange?.date_to,
    currentOrg,
  ]);

  React.useEffect(() => {
    let alive = true;
    setSeries(null);
    setError("");
    if (!selectionReady || !slot.param)
      return () => {
        alive = false;
      };
    const params = {
      vendor: slot.vendor,
      sku: slot.sku,
      material_id: slot.material_id,
      param: slot.param,
      n: slot.n || 20,
      date_from: slot.dateRange?.date_from || undefined,
      date_to: slot.dateRange?.date_to || undefined,
    };
    api
      .get("/charts/series", { params })
      .then((res) => {
        if (alive) setSeries(res.data || null);
      })
      .catch(() => {
        if (alive) {
          setSeries(null);
          setError("Unable to load chart data for this selection.");
        }
      });
    return () => {
      alive = false;
    };
  }, [
    selectionReady,
    slot.vendor,
    slot.sku,
    slot.material_id,
    slot.param,
    slot.n,
    slot.dateRange?.date_from,
    slot.dateRange?.date_to,
    currentOrg,
  ]);

  const numeric = isNumericSeries(series);
  const maxLots = totalLots ? Math.min(totalLots, 20) : 20;
  const detailsColumns = numeric
    ? [
        { key: "lot", label: t("lot") },
        { key: "result", label: "Result" },
        { key: "lower", label: "Lower Limit" },
        { key: "upper", label: "Upper Limit" },
      ]
    : [
        { key: "lot", label: t("lot") },
        { key: "result", label: "Result" },
      ];
  const detailsRows = React.useMemo(() => {
    const lots = series?.lots || [];
    return lots.map((lot, index) => ({
      lot,
      result: numeric
        ? (series?.result || [])[index]
        : (series?.raw || [])[index],
      lower: (series?.lower || [])[index],
      upper: (series?.upper || [])[index],
    }));
  }, [series, numeric]);

  const updateDateRange = (next) => onChange({ ...slot, dateRange: next });
  const updateSlotSelection = (rowKey) => {
    const row = optionRows.find((item) => rowIdentityKey(item) === rowKey);
    if (!row) {
      onChange({
        ...slot,
        vendor: "",
        material: "",
        sku: "",
        material_id: "",
        rowKey: "",
        param: "",
      });
      return;
    }
    onChange({
      ...slot,
      vendor: row.vendor,
      material: row.material,
      sku: row.sku,
      material_id: realMaterialId(row),
      rowKey,
      param: "",
    });
  };

  const parameterLabel = React.useMemo(() => {
    const spec = specs.find((item) => item?.parameter_key === slot.param);
    return spec?.Parameter || slot.param || "chart";
  }, [specs, slot.param]);

  const nonNumericExportRows = () => {
    const lots = series?.lots || [];
    const raw = series?.raw || [];
    const header = [
      t("vendor"),
      t("material"),
      "SKU",
      "Parameter",
      t("lot"),
      "Result",
    ];
    const rows = lots.map((lot, index) => [
      slot.vendor,
      slot.material,
      slot.sku,
      parameterLabel,
      lot,
      raw[index] ?? "",
    ]);
    return [header, ...rows];
  };

  const downloadNonNumericCsv = () => {
    setExportError("");
    downloadCsvRows(
      `${safeFilePart(label)}_${safeFilePart(parameterLabel)}.csv`,
      nonNumericExportRows(),
    );
  };

  const downloadNonNumericExcel = async () => {
    setExportError("");
    try {
      await downloadExcelRows(
        `${safeFilePart(label)}_${safeFilePart(parameterLabel)}.xlsx`,
        nonNumericExportRows(),
        { sheetName: "Chart Data" },
      );
    } catch (_) {
      setExportError("Could not create the Excel export.");
    }
  };

  const chartContent = ({ expanded }) => {
    if (!selectionReady)
      return (
        <div className="chart-empty-state">
          Select {t("vendor")} / {t("material")} / SKU to load chart parameters.
        </div>
      );
    if (!slot.param)
      return (
        <div className="chart-empty-state">
          Select a parameter to render this chart.
        </div>
      );
    if (error)
      return <div className="chart-empty-state error-text">{error}</div>;
    if (!series)
      return <div className="chart-empty-state">Loading chart data…</div>;
    if (!series.lots?.length)
      return (
        <div className="chart-empty-state">
          No approved {t("lot").toLowerCase()} data found for this
          parameter/date range.
        </div>
      );
    if (numeric) {
      return (
        <div
          ref={!expanded ? chartCanvasRef : undefined}
          className={
            expanded ? "chart-js-wrap chart-js-wrap--expanded" : "chart-js-wrap"
          }
        >
          <Line
            data={{
              labels: series.lots,
              datasets: [
                {
                  label: "Lower Limit",
                  data: series.lower,
                  borderColor: "#1f77b4",
                  pointRadius: 3,
                  borderWidth: 2.5,
                  tension: 0.2,
                },
                {
                  label: "Result",
                  data: series.result,
                  borderColor: "#111111",
                  pointRadius: 4,
                  borderWidth: 3,
                  tension: 0.2,
                },
                {
                  label: "Upper Limit",
                  data: series.upper,
                  borderColor: "#d62728",
                  pointRadius: 3,
                  borderWidth: 2.5,
                  tension: 0.2,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { position: "top" },
                tooltip: { mode: "index", intersect: false },
              },
              interaction: { mode: "nearest", intersect: false },
              scales: { x: { ticks: { maxRotation: 45, minRotation: 0 } } },
            }}
          />
        </div>
      );
    }
    return (
      <div className="table--card chart-non-numeric-table">
        <div className="table-scroll">
          <table className="table table--dense">
            <thead>
              <tr>
                <th>{t("lot")}</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {(series.lots || []).map((lot, i) => (
                <tr key={`${lot}-${i}`}>
                  <td>{lot}</td>
                  <td>{(series.raw || [])[i] ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const detailsButton = (
    <DetailsModal
      title={`${label} Details`}
      columns={detailsColumns}
      rows={detailsRows}
      emptyText="No chart details available yet."
    />
  );

  return (
    <div className="section chart-slot-card">
      <div className="chart-slot-header">
        <div>
          <h3>{label}</h3>
          {slot.lockedSelection ? (
            <div className="chart-desc">{describeRow(slot)}</div>
          ) : null}
        </div>
        <button className="btn btn-secondary" type="button" onClick={onRemove}>
          Remove
        </button>
      </div>

      <div className="chart-slot-controls">
        {slot.lockedSelection ? (
          <div className="selected-lot-summary chart-slot-summary">
            <div>
              <span>{t("vendor")}</span>
              <strong>{slot.vendor || "—"}</strong>
            </div>
            <div>
              <span>{t("material")}</span>
              <strong>{slot.material || "—"}</strong>
            </div>
            <div>
              <span>SKU</span>
              <strong>{slot.sku || "—"}</strong>
            </div>
          </div>
        ) : (
          <label className="field-grow">
            <span>
              {t("vendor")} / {t("material")} / SKU
            </span>
            <select
              value={slot.rowKey || ""}
              onChange={(event) => updateSlotSelection(event.target.value)}
            >
              <option value="">Select a row…</option>
              {optionRows.map((row) => (
                <option key={rowIdentityKey(row)} value={rowIdentityKey(row)}>
                  {describeRow(row)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          <span>Parameter</span>
          <select
            value={slot.param || ""}
            onChange={(event) =>
              onChange({ ...slot, param: event.target.value })
            }
            disabled={!selectionReady || !specs.length}
          >
            <option value="">Select parameter…</option>
            {specs.map((spec) => {
              const value = spec.parameter_key || spec.Parameter;
              return (
                <option key={value || spec.Parameter} value={value}>
                  {spec.Parameter}
                </option>
              );
            })}
          </select>
        </label>

        <DateRangePicker
          value={slot.dateRange || emptyDateRange}
          onChange={updateDateRange}
          label="Date range"
        />

        <label>
          <span>{tp("lot")} to show</span>
          <input
            type="number"
            min={1}
            max={maxLots}
            value={Math.min(slot.n || 20, maxLots)}
            onWheel={preventNumberWheel}
            onChange={(event) =>
              onChange({
                ...slot,
                n: Math.max(
                  1,
                  Math.min(
                    maxLots,
                    parseInt(event.target.value || "1", 10) || 1,
                  ),
                ),
              })
            }
          />
          <small>
            {totalLots
              ? `${totalLots} ${tp("lot").toLowerCase()} available`
              : `No ${tp("lot").toLowerCase()} available`}
          </small>
        </label>

        <button
          className="btn btn-secondary"
          type="button"
          onClick={() => onCopyDatesToAll(slot.dateRange || emptyDateRange)}
        >
          Copy dates to all
        </button>
      </div>

      <ExpandableChart
        title={`${label}${slot.param ? ` — ${slot.param}` : ""}`}
        subtitle={describeRow(slot)}
        renderChart={chartContent}
        footer={
          <div className="hstack chart-slot-footer-actions">
            {detailsButton}
            {exportError ? (
              <span className="helper-text" role="alert">
                {exportError}
              </span>
            ) : null}
            {numeric && series?.lots?.length ? (
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  const el =
                    chartCanvasRef.current?.querySelector("canvas") ||
                    chartCanvasRef.current;
                  if (!el) return;
                  const pdf = new jsPDF({
                    orientation: "landscape",
                    unit: "pt",
                    format: "a4",
                  });
                  const pw = pdf.internal.pageSize.getWidth();
                  const ph = pdf.internal.pageSize.getHeight();
                  const desc = [
                    slot.vendor,
                    slot.material,
                    slot.sku,
                    slot.param,
                  ]
                    .filter(Boolean)
                    .join(" • ");
                  pdf.setFontSize(14);
                  pdf.text(label, 20, 26);
                  if (desc) {
                    pdf.setFontSize(10);
                    pdf.text(desc, 20, 42);
                  }
                  const img = el.toDataURL("image/png", 1.0);
                  const ratio = el.width / el.height;
                  let w = pw - 40,
                    h = (pw - 40) / ratio;
                  const top = desc ? 52 : 40;
                  if (h > ph - (top + 30)) {
                    h = ph - (top + 30);
                    w = h * ratio;
                  }
                  pdf.addImage(img, "PNG", (pw - w) / 2, top, w, h);
                  pdf.setFontSize(8);
                  pdf.text(
                    `Generated: ${formatDateTime(new Date())}`,
                    20,
                    ph - 16,
                  );
                  pdf.save(`${label.replace(/[\s/()]+/g, "_")}.pdf`);
                }}
              >
                Download PDF
              </button>
            ) : null}
            {!numeric && series?.lots?.length ? (
              <>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={downloadNonNumericCsv}
                >
                  Download CSV
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={downloadNonNumericExcel}
                >
                  Download Excel
                </button>
              </>
            ) : null}
          </div>
        }
      />
    </div>
  );
}

export default function ChartsPage() {
  const { user } = useAuth();
  const { current: currentOrg } = useOrg();
  const { t } = useTerminology();
  const optionRows = useChartMaterialRows(user, currentOrg);
  const [selectionOpen, setSelectionOpen] = React.useState(true);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [chartCounts, setChartCounts] = React.useState({});
  const [sortBy, setSortBy] = React.useState("vendor");
  const [sortOrder, setSortOrder] = React.useState("asc");
  const [groups, setGroups] = React.useState([]);
  const flatSlotCount = groups.reduce((sum, g) => sum + g.slots.length, 0);

  React.useEffect(() => {
    const valid = new Set(optionRows.map(rowIdentityKey));
    const next = selectedIds.filter((id) => valid.has(String(id)));
    if (
      next.length !== selectedIds.length ||
      next.some((id, idx) => id !== selectedIds[idx])
    ) {
      setSelectedIds(next);
    }
  }, [optionRows, selectedIds]);

  const selectedRows = React.useMemo(() => {
    const byId = new Map(optionRows.map((row) => [rowIdentityKey(row), row]));
    return selectedIds.map((id) => byId.get(String(id))).filter(Boolean);
  }, [optionRows, selectedIds]);

  const selectionColumns = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor") },
      { key: "material", label: t("material") },
      { key: "sku", label: "SKU" },
      {
        key: "charts",
        label: "Charts",
        value: (row) => chartCounts[rowIdentityKey(row)] || 1,
        render: (row) => {
          const id = rowIdentityKey(row);
          return (
            <input
              className="chart-count-input"
              type="number"
              min={1}
              max={4}
              value={chartCounts[id] || 1}
              onClick={(event) => event.stopPropagation()}
              onWheel={preventNumberWheel}
              onChange={(event) => {
                const value = clampChartCount(event.target.value);
                setChartCounts((current) => ({ ...current, [id]: value }));
              }}
            />
          );
        },
      },
    ],
    [chartCounts, t],
  );
  const selectionSortOptions = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor"), value: (row) => row.vendor },
      { key: "material", label: t("material"), value: (row) => row.material },
      { key: "sku", label: "SKU", value: (row) => row.sku },
      {
        key: "charts",
        label: "Charts",
        value: (row) => chartCounts[rowIdentityKey(row)] || 1,
      },
    ],
    [chartCounts, t],
  );

  const applySelection = () => {
    const nextGroups = selectedRows.map((row) => {
      const count = clampChartCount(chartCounts[rowIdentityKey(row)] || 1);
      return {
        id: groupId(),
        sourceKey: rowIdentityKey(row),
        slots: Array.from({ length: count }, () => makeSlotFromRow(row)),
      };
    });
    setGroups(nextGroups);
    if (nextGroups.length) setSelectionOpen(false);
  };

  const addBlankChart = () => {
    setGroups((current) => [
      ...current,
      { id: groupId(), sourceKey: "", slots: [makeBlankSlot()] },
    ]);
  };

  const updateSlot = (groupIndex, slotIndex, nextSlot) => {
    setGroups((current) =>
      current.map((group, gi) => {
        if (gi !== groupIndex) return group;
        return {
          ...group,
          slots: group.slots.map((slot, si) =>
            si === slotIndex ? nextSlot : slot,
          ),
        };
      }),
    );
  };

  const removeSlot = (groupIndex, slotIndex) => {
    setGroups((current) =>
      current
        .map((group, gi) =>
          gi === groupIndex
            ? {
                ...group,
                slots: group.slots.filter((_, si) => si !== slotIndex),
              }
            : group,
        )
        .filter((group) => group.slots.length),
    );
  };

  const copyDatesToAll = (dateRange) => {
    setGroups((current) =>
      current.map((group) => ({
        ...group,
        slots: group.slots.map((slot) => ({
          ...slot,
          dateRange: { ...(dateRange || emptyDateRange) },
        })),
      })),
    );
  };

  const printAllCharts = async () => {
    const root = document.getElementById("charts-capture-root");
    if (!root) return;
    const canvas = await captureNodeToCanvas(root);
    splitTallCanvasToPDF(canvas, "charts.pdf", { orientation: "landscape" });
  };

  return (
    <div className="page-stack charts-page">
      <PageHeader
        eyebrow="Analytics"
        icon="charts"
        title="Charts"
        subtitle={`Build approved ${t("lot").toLowerCase()} trend charts from selected ${t("vendor")} / ${t("material")} / SKU rows.`}
      />

      <details
        className="selection-expander charts-selection-expander"
        open={selectionOpen}
        onToggle={(event) => setSelectionOpen(event.currentTarget.open)}
      >
        <summary>
          Select {t("vendor")} / {t("material")} / SKU for Charts
        </summary>
        <LotSelectionTable
          rows={optionRows}
          columns={selectionColumns}
          sortOptions={selectionSortOptions}
          sortBy={sortBy}
          onSortByChange={setSortBy}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          selectionMode="multi"
          selected={selectedIds}
          onSelectionChange={setSelectedIds}
          getRowId={rowIdentityKey}
          showOrderBadges={true}
          searchPlaceholder={`Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, or SKU`}
          emptyText={`No approved ${t("material").toLowerCase()}/SKU rows found.`}
          pageSize={10}
        />
        <div className="card-actions chart-selection-actions">
          <div className="helper-text">
            Selected: {selectedRows.length} row
            {selectedRows.length === 1 ? "" : "s"}. Each selected row creates
            1–4 chart slots based on the Charts count.
          </div>
          <div className="hstack">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={!selectedIds.length}
            >
              Clear Selection
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={applySelection}
              disabled={!selectedRows.length}
            >
              Apply Selection
            </button>
          </div>
        </div>
      </details>

      <div className="section chart-page-toolbar-section">
        <div className="chart-page-toolbar">
          <button
            className="btn btn-secondary"
            type="button"
            onClick={addBlankChart}
          >
            Add Chart
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={printAllCharts}
            disabled={!groups.length}
            title="Download all charts as PDF"
            aria-label="Download charts PDF"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 9V4h12v5M6 14h12v6H6v-6Zm-2 0h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2Z"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            <span>Download PDF</span>
          </button>
          <div className="helper-text">
            Selection controls stay outside PDF export. Generated chart slots
            below are captured.
          </div>
        </div>

        <div id="charts-capture-root" className="charts-capture-root">
          {groups.length ? (
            (() => {
              let flatIndex = 0;
              return groups.map((group, groupIndex) => {
                const letter = groupLetter(groupIndex);
                const total = group.slots.length;
                return (
                  <div className="chart-slot-group" key={group.id}>
                    {group.slots.map((slot, slotIndex) => {
                      const label =
                        total > 1
                          ? `${letter} (${slotIndex + 1}/${total})`
                          : letter;
                      const fi = flatIndex++;
                      const isLastOdd =
                        flatSlotCount % 2 === 1 && fi === flatSlotCount - 1;
                      return (
                        <div
                          key={slot.id}
                          style={
                            isLastOdd ? { gridColumn: "1 / -1" } : undefined
                          }
                        >
                          <ChartSlot
                            slot={slot}
                            label={label}
                            optionRows={optionRows}
                            onChange={(nextSlot) =>
                              updateSlot(groupIndex, slotIndex, nextSlot)
                            }
                            onRemove={() => removeSlot(groupIndex, slotIndex)}
                            onCopyDatesToAll={copyDatesToAll}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()
          ) : (
            <div className="chart-empty-state chart-empty-state--large">
              Select rows above and click Apply Selection, or use Add Chart to
              create a blank chart slot.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
